import React, { useState, useEffect, useRef, useMemo } from 'react';
import AssetService from '../../services/AssetService';
import FacilityService from '../../services/FacilityService';
import AssetInventoryService from '../../services/AssetInventoryService';
import { If } from "../../utils/Controls";
import { AntdSelect } from "../../utils/Antd";
import util from "../../utils/util";
import search from '../assets/icons/magnifying-glass-solid.svg'
import { Bar, Line, Pie, Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';
import { 
    Button, 
    message, 
    Tabs, 
    Card, 
    Row, 
    Col, 
    Switch, 
    Dropdown, 
    Space, 
    Alert, 
    Tooltip as AntTooltip,
    Select,
    Radio,
    Modal,
    Input,
    List,
    Tag,
    Popover,
    Badge,
    Table
} from 'antd';
import { 
    DownloadOutlined, 
    FilePdfOutlined, 
    ReloadOutlined, 
    ExpandOutlined,
    CompressOutlined,
    SettingOutlined,
    BellOutlined,
    LineChartOutlined,
    BarChartOutlined,
    PieChartOutlined,
    CloudSyncOutlined,
    DashboardOutlined,
    TrophyOutlined,
    AlertOutlined,
    ToolOutlined,
    AppstoreOutlined,
    EnvironmentOutlined,
    GlobalOutlined,
    InboxOutlined,
    SaveOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SwapOutlined,
    WifiOutlined,
    ExportOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    DragOutlined,
    CommentOutlined,
    TagOutlined,
    TableOutlined,
    AreaChartOutlined,
    DotChartOutlined,
    ColumnHeightOutlined
} from '@ant-design/icons';
import '../assets/css/styles.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

// Chatbot Components
const DashboardChatbot = ({ 
    visible, 
    onClose, 
    dashboardData, 
    currentTab, 
    selectedGrouping,
    darkMode = false 
}) => {
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "Hello! I'm your dashboard assistant. I can help you analyze your asset data, explain charts, or guide you through the dashboard features. What would you like to know?",
            sender: 'bot',
            timestamp: new Date(),
            suggestions: [
                "Show me asset summary",
                "Explain current chart",
                "How to export data?",
                "Filter suggestions"
            ]
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const generateResponse = async (userMessage) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const lowerMessage = userMessage.toLowerCase();
        let response = "";
        let suggestions = [];

        if (lowerMessage.includes('summary') || lowerMessage.includes('overview')) {
            response = `Here's your ${currentTab} asset summary:\n\n`;
            if (dashboardData && dashboardData.length > 0) {
                const total = dashboardData.reduce((sum, item) => sum + (item.totalitems || 0), 0);
                response += `• Total ${currentTab} assets: ${total}\n`;
                response += `• Grouped by: ${selectedGrouping}\n`;
                response += `• Top location: ${dashboardData[0]?.client_name || dashboardData[0]?.item_station_point || 'N/A'}\n`;
                response += `\nWould you like me to explain any specific metrics?`;
            } else {
                response += "No data available for the current filters. Try adjusting your search criteria.";
            }
            suggestions = ["Show trends", "Export this data", "Change grouping"];
            
        } else if (lowerMessage.includes('export') || lowerMessage.includes('download')) {
            response = "You can export your charts in multiple formats:\n\n• PNG: High-quality image format\n• JPG: Compressed image format\n• PDF: Document with chart and metadata\n• CSV: Raw data export\n\nLook for the green 'Export' button above your chart.";
            suggestions = ["Show chart options", "How to save views?", "Print dashboard"];
            
        } else if (lowerMessage.includes('chart') || lowerMessage.includes('visualization')) {
            response = `Your current chart shows ${currentTab} assets grouped by ${selectedGrouping}.\n\nAvailable chart types:\n• Bar Chart - Compare values across categories\n• Line Chart - Show trends over time\n• Pie Chart - Display proportions\n• Stacked Column - Show composition\n• Year over Year - Compare periods\n• Scatter Plot - Distribution analysis\n• Pivot Table - Detailed data view\n\nThe colors represent different asset conditions or statuses.`;
            suggestions = ["Change chart type", "Explain colors", "Save current view"];
            
        } else {
            response = "I understand you're asking about dashboard functionality. I can help you with:\n\n• Analyzing your asset data\n• Explaining chart features\n• Guiding you through filters\n• Showing export options\n• Dashboard navigation tips\n\nCould you be more specific about what you'd like to know?";
            suggestions = ["Show data summary", "Chart help", "Export guide", "Filter tips"];
        }

        setIsLoading(false);
        return { response, suggestions };
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        const userMessage = {
            id: messages.length + 1,
            text: inputValue,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');

        const { response, suggestions } = await generateResponse(inputValue);

        const botMessage = {
            id: messages.length + 2,
            text: response,
            sender: 'bot',
            timestamp: new Date(),
            suggestions: suggestions
        };

        setMessages(prev => [...prev, botMessage]);
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        setTimeout(() => {
            handleSendMessage();
        }, 100);
    };

    const clearChat = () => {
        setMessages([
            {
                id: 1,
                text: "Chat cleared! How can I help you with your dashboard?",
                sender: 'bot',
                timestamp: new Date(),
                suggestions: [
                    "Show me asset summary",
                    "Explain current chart", 
                    "How to export data?",
                    "Filter suggestions"
                ]
            }
        ]);
    };

    const exportChat = () => {
        const chatContent = messages.map(msg => 
            `[${msg.timestamp.toLocaleTimeString()}] ${msg.sender === 'bot' ? 'Assistant' : 'You'}: ${msg.text}`
        ).join('\n\n');
        
        const blob = new Blob([chatContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dashboard-chat-${new Date().toISOString().split('T')[0]}.txt`;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (!visible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '400px',
            height: '100vh',
            backgroundColor: darkMode ? '#1a1a1a' : '#ffffff',
            boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            transform: visible ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 0.3s ease'
        }}>
            <div style={{
                padding: '16px',
                borderBottom: `1px solid ${darkMode ? '#333' : '#e0e0e0'}`,
                backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px'
                    }}>
                        🤖
                    </div>
                    <span style={{ 
                        fontWeight: 'bold', 
                        color: darkMode ? '#fff' : '#333' 
                    }}>
                        Dashboard Assistant
                    </span>
                    <span style={{
                        backgroundColor: '#52c41a',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px'
                    }}>
                        AI Powered
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={exportChat} style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: darkMode ? '#ccc' : '#666'
                    }} title="Export Chat">💾</button>
                    <button onClick={clearChat} style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: darkMode ? '#ccc' : '#666'
                    }} title="Clear Chat">🗑️</button>
                    <button onClick={onClose} style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        color: darkMode ? '#ccc' : '#666'
                    }} title="Close">✕</button>
                </div>
            </div>

            <div style={{ 
                padding: '12px 16px', 
                backgroundColor: darkMode ? '#2a2a2a' : '#e6f7ff',
                borderBottom: `1px solid ${darkMode ? '#333' : '#d9d9d9'}`,
                fontSize: '12px',
                color: darkMode ? '#ccc' : '#666'
            }}>
                Context: {currentTab} • {selectedGrouping} • {dashboardData?.length || 0} items
            </div>

            <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '16px',
                backgroundColor: darkMode ? '#1a1a1a' : '#ffffff'
            }}>
                {messages.map((message) => (
                    <div 
                        key={message.id}
                        style={{ 
                            display: 'flex', 
                            flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                            alignItems: 'flex-start',
                            marginBottom: '16px',
                            gap: '8px'
                        }}
                    >
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: message.sender === 'bot' ? '#1890ff' : '#52c41a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '14px',
                            flexShrink: 0
                        }}>
                            {message.sender === 'bot' ? '🤖' : '👤'}
                        </div>
                        
                        <div style={{ 
                            maxWidth: '80%',
                            textAlign: message.sender === 'user' ? 'right' : 'left'
                        }}>
                            <div style={{
                                backgroundColor: message.sender === 'user' 
                                    ? '#1890ff' 
                                    : darkMode ? '#2a2a2a' : '#f0f0f0',
                                color: message.sender === 'user' ? '#fff' : darkMode ? '#fff' : '#000',
                                padding: '8px 12px',
                                borderRadius: '12px',
                                marginBottom: message.suggestions ? '8px' : '0',
                                fontSize: '13px',
                                whiteSpace: 'pre-line'
                            }}>
                                {message.text}
                            </div>
                            
                            {message.suggestions && (
                                <div style={{ 
                                    display: 'flex', 
                                    flexWrap: 'wrap', 
                                    gap: '4px',
                                    marginTop: '8px'
                                }}>
                                    {message.suggestions.map((suggestion, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            style={{ 
                                                fontSize: '11px',
                                                padding: '4px 8px',
                                                borderRadius: '12px',
                                                border: `1px dashed ${darkMode ? '#666' : '#ccc'}`,
                                                backgroundColor: 'transparent',
                                                color: darkMode ? '#ccc' : '#666',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            )}
                            
                            <div style={{ 
                                fontSize: '10px', 
                                opacity: 0.6,
                                marginTop: '4px',
                                color: darkMode ? '#ccc' : '#666'
                            }}>
                                {message.timestamp.toLocaleTimeString()}
                            </div>
                        </div>
                    </div>
                ))}
                
                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '16px' }}>
                        <div style={{ 
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            border: `2px solid ${darkMode ? '#333' : '#f3f3f3'}`,
                            borderTop: '2px solid #1890ff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <div style={{ 
                            marginTop: '8px', 
                            fontSize: '12px',
                            color: darkMode ? '#ccc' : '#666'
                        }}>
                            Assistant is thinking...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div style={{ 
                padding: '16px',
                borderTop: `1px solid ${darkMode ? '#333' : '#d9d9d9'}`,
                backgroundColor: darkMode ? '#2a2a2a' : '#ffffff'
            }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                        type="text"
                        placeholder="Ask about your dashboard..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        disabled={isLoading}
                        style={{ 
                            flex: 1,
                            padding: '8px 12px',
                            borderRadius: '20px',
                            border: `1px solid ${darkMode ? '#555' : '#d9d9d9'}`,
                            backgroundColor: darkMode ? '#1a1a1a' : '#ffffff',
                            color: darkMode ? '#fff' : '#000',
                            outline: 'none'
                        }}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={isLoading || !inputValue.trim()}
                        style={{
                            padding: '8px 12px',
                            borderRadius: '20px',
                            border: 'none',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                            opacity: isLoading || !inputValue.trim() ? 0.5 : 1
                        }}
                    >
                        {isLoading ? '⏳' : '🚀'}
                    </button>
                </div>
                
                <div style={{ 
                    display: 'flex', 
                    gap: '4px',
                    flexWrap: 'wrap'
                }}>
                    <button 
                        onClick={() => handleSuggestionClick('Explain current chart')}
                        style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: darkMode ? '#ccc' : '#666',
                            cursor: 'pointer',
                            borderRadius: '4px'
                        }}
                    >
                        📊 Chart Help
                    </button>
                    <button 
                        onClick={() => handleSuggestionClick('Show me asset summary')}
                        style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: darkMode ? '#ccc' : '#666',
                            cursor: 'pointer',
                            borderRadius: '4px'
                        }}
                    >
                        📋 Summary
                    </button>
                    <button 
                        onClick={() => handleSuggestionClick('Give me recommendations')}
                        style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: darkMode ? '#ccc' : '#666',
                            cursor: 'pointer',
                            borderRadius: '4px'
                        }}
                    >
                        💡 Tips
                    </button>
                </div>
            </div>

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

const ChatFloatingButton = ({ onClick, unreadCount = 0, darkMode = false }) => {
    return (
        <div style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            zIndex: 1000,
        }}>
            {unreadCount > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    backgroundColor: '#ff4d4f',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    zIndex: 1
                }}>
                    {unreadCount}
                </div>
            )}
            <button
                onClick={onClick}
                style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#1890ff',
                    color: 'white',
                    fontSize: '20px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                    e.target.style.transform = 'scale(1.1)';
                    e.target.style.backgroundColor = '#40a9ff';
                }}
                onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.backgroundColor = '#1890ff';
                }}
            >
                💬
            </button>
        </div>
    );
};

const GROUPING_CONFIGS = {
  facilities: {
    title: 'GROUPED BY FACILITIES',
    backgroundColor: 'rgba(255, 99, 71, 0.1)',
    apiMethod: 'lists8',
    labelKey: 'client_name',
    chartLabels: { x: 'FACILITIES', y: 'TOTALS' },
    icon: <AppstoreOutlined />,
    description: 'View assets grouped by facility/client'
  },
  locations: {
    title: 'GROUPED BY LOCATION',
    backgroundColor: 'rgba(173, 216, 230, 0.2)',
    apiMethod: 'lists7',
    labelKey: 'item_station_point',
    chartLabels: { x: 'LOCATION', y: 'TOTALS' },
    icon: <EnvironmentOutlined />,
    description: 'View assets grouped by location/station point'
  },
  regions: {
    title: 'GROUPED BY REGIONS',
    backgroundColor: 'rgba(128, 0, 0, 0.2)',
    apiMethod: 'lists9',
    labelKey: 'client_region',
    chartLabels: { x: 'REGIONS', y: 'TOTALS' },
    icon: <GlobalOutlined />,
    description: 'View assets grouped by regional distribution'
  },
  items: {
    title: 'GROUPED BY ASSETS',
    backgroundColor: 'rgba(139, 69, 19, 0.1)',
    apiMethod: 'lists1',
    labelKey: 'item',
    chartLabels: { x: 'ASSETS', y: 'TOTALS' },
    icon: <InboxOutlined />,
    description: 'View assets grouped by asset type/item'
  }
};

const ASSET_CONFIGS = {
  '8': {
    name: 'Vehicle',
    datasets: [
      { key: 'fair', label: 'Fair', color: 'rgba(255, 165, 0, 1)' },
      { key: 'good', label: 'Good condition', color: 'rgba(9, 129, 45, 0.99)' },
      { key: 'repairs', label: 'Waiting for repairs', color: 'rgba(255, 0, 0, 1)' },
      { key: 'disposal', label: 'Awaiting for disposal', color: 'rgba(28, 3, 167, 0.8)' }
    ]
  },
  '9': {
    name: 'Supplies',
    datasets: [
      { key: 'usable', label: 'Usable', color: 'rgba(9, 129, 45, 0.99)' },
      { key: 'expired', label: 'Expired', color: 'rgba(255, 0, 0, 1)' }
    ]
  },
  '3': {
    name: 'Medical',
    datasets: [
      { key: 'functional', label: 'Functional', color: 'rgba(9, 129, 45, 0.99)' },
      { key: 'partialfunctional', label: 'Partial functional', color: 'rgba(184, 199, 88, 0.99)' },
      { key: 'installed', label: 'Installed', color: 'rgba(84, 88, 85, 0.99)' },
      { key: 'not_installed', label: 'Not installed', color: 'rgba(255, 165, 0, 1)' },
      { key: 'nonfunctional', label: 'Non functional', color: 'rgba(255, 0, 0, 1)' },
      { key: 'boarded', label: 'Boarded', color: 'rgba(28, 3, 167, 0.8)' }
    ]
  },
  '2': {
    name: 'Non Medical',
    datasets: [
      { key: 'functional', label: 'Serviceable', color: 'rgba(9, 129, 45, 0.99)' },
      { key: 'nonfunctional', label: 'Unserviceable', color: 'rgba(255, 0, 0, 1)' },
      { key: 'boarded', label: 'Boarded', color: 'rgba(28, 3, 167, 0.8)' }
    ]
  }
};

const Dashboards = () => {
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('dashboard-dark-mode') === 'true';
    });
    const [realTimeEnabled, setRealTimeEnabled] = useState(false);
    const [chartType, setChartType] = useState('line');
    const [selectedGrouping, setSelectedGrouping] = useState('facilities');
    const [kpiData, setKpiData] = useState({
        totalAssets: 0,
        criticalAlerts: 0,
        utilizationRate: 78,
        maintenanceScheduled: 12
    });

    const [savedViews, setSavedViews] = useState(() => {
        return JSON.parse(localStorage.getItem('dashboard-saved-views') || '[]');
    });
    const [showSaveViewModal, setShowSaveViewModal] = useState(false);
    const [viewName, setViewName] = useState('');
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [offlineData, setOfflineData] = useState(() => {
        return JSON.parse(localStorage.getItem('dashboard-offline-data') || '{}');
    });
    const [annotations, setAnnotations] = useState(() => {
        return JSON.parse(localStorage.getItem('dashboard-annotations') || '[]');
    });
    const [showAnnotationModal, setShowAnnotationModal] = useState(false);
    const [annotationText, setAnnotationText] = useState('');
    const [comparisonMode, setComparisonMode] = useState(false);
    const [comparisonData, setComparisonData] = useState(null);
    const [smartFilterCounts, setSmartFilterCounts] = useState({});

    const [chatVisible, setChatVisible] = useState(false);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [currentChartData, setCurrentChartData] = useState([]);

    const modules = util.getModules();

        const isSuper  = localStorage.getItem('is_admin')        === '1'
                  || localStorage.getItem('is_national')     === '1'
                  || localStorage.getItem('is_client_admin') === '1';
    const tabItems = [
        { k: 'Medical', v: 'Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '3' },
        { k: 'Non Medical', v: 'Non Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '2' },
        { k: 'Vehicle', v: 'Vehicle', cat_type: 'Med/NonMed', asset_cat_id: '8' },
      //  { k: 'Supplies', v: 'Supplies', cat_type: 'Med/NonMed', asset_cat_id: '9' }
    ];
   const filteredItems = useMemo(() => {
        if (isSuper) return tabItems;
        return tabItems.filter(item => {
            if (item.k === 'Medical' && modules.medical === 1) return true;
            if (item.k === 'Non Medical' && modules.non_medical === 1) return true;
            if (item.k === 'Vehicle' && modules.vehicle === 1) return true;
           // if (item.k === 'Supplies' && modules.supplies === 1) return true;
            return false;
        });
    }, [modules, isSuper]);

    const [tab, setTab] = useState(filteredItems.length > 0 ? filteredItems[0].k : '');

    useEffect(() => {
        localStorage.setItem('dashboard-dark-mode', darkMode.toString());
        document.body.className = darkMode ? 'dark-mode' : '';
    }, [darkMode]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);
        
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        if (!isOffline && Object.keys(offlineData).length > 0) {
            localStorage.setItem('dashboard-offline-data', JSON.stringify(offlineData));
        }
    }, [offlineData, isOffline]);

    useEffect(() => {
        localStorage.setItem('dashboard-annotations', JSON.stringify(annotations));
    }, [annotations]);

    useEffect(() => {
        localStorage.setItem('dashboard-saved-views', JSON.stringify(savedViews));
    }, [savedViews]);

    useEffect(() => {
        if (realTimeEnabled) {
            const interval = setInterval(() => {
                setKpiData(prev => ({
                    ...prev,
                    totalAssets: Math.max(0, prev.totalAssets + Math.floor(Math.random() * 6) - 3),
                    criticalAlerts: Math.max(0, prev.criticalAlerts + Math.floor(Math.random() * 3) - 1),
                    utilizationRate: Math.min(100, Math.max(0, prev.utilizationRate + Math.floor(Math.random() * 10) - 5))
                }));
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [realTimeEnabled]);

    const onTabChange = (key) => {
        setTab(key);
    };

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [facilitiesRes, itemsRes, stationPointsRes, functionalStatusRes] = await Promise.all([
                    FacilityService.all({ status: 1, include_me: 'Y' }),
                    AssetService.allItems({ status: 1 }),
                    FacilityService.allStationPoints({ status: 1 }),
                    AssetService.allFunctionalStatus()
                ]);

                setClients(facilitiesRes.data.result.data.map(v => ({ 
                    id: v.id, 
                    name: v.business_name, 
                    is_national: v.is_national, 
                    is_me: v.is_me 
                })));

                setItems(itemsRes.data.result.data.map(v => ({
                    ...v,
                    item_full_name: v.cat_type === 'Med/NonMed' 
                        ? `${v.item_full_name} [${v.cat}]`
                        : `${v.item_full_name} [${v.subcat}]`
                })));

                setStationPoints(stationPointsRes.data.result.data);
                setFunctionalStatus(functionalStatusRes.data.data);

                setKpiData(prev => ({
                    ...prev,
                    totalAssets: itemsRes.data.result.data.length
                }));
            } catch (error) {
                message.error('Failed to load initial data: ' + error.message);
            }
        };

        loadInitialData();
    }, []);

    const getDashboardTitle = () => {
        const activeTab = filteredItems.find(item => item.k === tab);
        return activeTab ? `${activeTab.v} Inventory Dashboard` : 'Assets Inventory Dashboard';
    };

    const saveCurrentView = () => {
        if (!viewName.trim()) {
            message.error('Please enter a view name');
            return;
        }
        
        const currentView = {
            id: Date.now(),
            name: viewName,
            grouping: selectedGrouping,
            chartType: chartType,
            tab: tab,
            timestamp: new Date().toISOString()
        };
        
        setSavedViews(prev => [...prev, currentView]);
        setViewName('');
        setShowSaveViewModal(false);
        message.success(`View "${viewName}" saved successfully`);
    };

    const loadSavedView = (view) => {
        setSelectedGrouping(view.grouping);
        setChartType(view.chartType);
        setTab(view.tab);
        message.success(`Loaded view "${view.name}"`);
    };

    const deleteSavedView = (viewId) => {
        setSavedViews(prev => prev.filter(v => v.id !== viewId));
        message.success('View deleted');
    };

    const addAnnotation = () => {
        if (!annotationText.trim()) {
            message.error('Please enter annotation text');
            return;
        }
        
        const newAnnotation = {
            id: Date.now(),
            text: annotationText,
            grouping: selectedGrouping,
            tab: tab,
            timestamp: new Date().toISOString(),
            author: 'Current User'
        };
        
        setAnnotations(prev => [...prev, newAnnotation]);
        setAnnotationText('');
        setShowAnnotationModal(false);
        message.success('Annotation added');
    };

    const deleteAnnotation = (annotationId) => {
        setAnnotations(prev => prev.filter(a => a.id !== annotationId));
        message.success('Annotation deleted');
    };

    return (
        <div className={`page-content modern-dashboard ${darkMode ? 'dark-mode' : ''}`}>
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>{getDashboardTitle()}</h2>
                </div>
                <div className="my-auto ml-auto"></div>
            </div>

            <div className="page-pad">
                {isOffline && (
                    <Alert
                        message="Offline Mode"
                        description="You're currently offline. Showing cached data when available."
                        type="warning"
                        showIcon
                        icon={<WifiOutlined />}
                        style={{ marginBottom: '16px' }}
                    />
                )}

                <Tabs
                    activeKey={tab}
                    destroyInactiveTabPane
                    onChange={onTabChange}
                    items={filteredItems.map(rw => ({
                        key: rw.k,
                        label: rw.v,
                        children: (
                            <>
                                <ConsolidatedAssetChart
                                    groupingType={selectedGrouping}
                                    items={items}
                                    stationPoints={stationPoints}
                                    functionalStatus={functionalStatus}
                                    cat_type={rw.cat_type}
                                    cat={rw.k}
                                    asset_cat_id={rw.asset_cat_id}
                                    clients={clients}
                                    chartType={chartType}
                                    darkMode={darkMode}
                                    onChartTypeChange={setChartType}
                                    selectedGrouping={selectedGrouping}
                                    onGroupingChange={setSelectedGrouping}
                                    onDataChange={setCurrentChartData}
                                    comparisonMode={comparisonMode}
                                    isOffline={isOffline}
                                    offlineData={offlineData}
                                    smartFilterCounts={smartFilterCounts}
                                />

                                <Card 
                                    size="small" 
                                    title="Advanced Features"
                                    style={{ 
                                        marginTop: '16px',
                                        marginBottom: '16px', 
                                        borderRadius: '8px',
                                        background: darkMode ? '#1f1f1f' : '#ffffff',
                                        border: darkMode ? '1px solid #333' : '1px solid #d9d9d9'
                                    }}
                                    bodyStyle={{ padding: '12px' }}
                                >
                                    <Row gutter={[16, 8]} align="middle">
                                        <Col xs={24} sm={6}>
                                            <Space>
                                                <Button 
                                                    size="small" 
                                                    icon={<SaveOutlined />}
                                                    onClick={() => setShowSaveViewModal(true)}
                                                >
                                                    Save View
                                                </Button>
                                                <Dropdown
                                                    menu={{
                                                        items: savedViews.map(view => ({
                                                            key: view.id,
                                                            label: (
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: '200px' }}>
                                                                    <span onClick={() => loadSavedView(view)}>{view.name}</span>
                                                                    <Button 
                                                                        size="small" 
                                                                        type="text" 
                                                                        danger 
                                                                        icon={<DeleteOutlined />}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            deleteSavedView(view.id);
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }))
                                                    }}
                                                    disabled={savedViews.length === 0}
                                                >
                                                    <Button size="small" icon={<EditOutlined />}>
                                                        Saved Views ({savedViews.length})
                                                    </Button>
                                                </Dropdown>
                                            </Space>
                                        </Col>

                                        <Col xs={24} sm={6}>
                                            <Space>
                                                <Dropdown
                                                    menu={{
                                                        items: [
                                                            {
                                                                key: 'png',
                                                                label: (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ 
                                                                            backgroundColor: '#52c41a', 
                                                                            color: 'white', 
                                                                            padding: '2px 6px', 
                                                                            borderRadius: '3px', 
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold'
                                                                        }}>PNG</span>
                                                                        High quality image
                                                                    </div>
                                                                ),
                                                                onClick: () => {
                                                                    const chartCanvas = document.querySelector('canvas');
                                                                    if (chartCanvas) {
                                                                        const link = document.createElement('a');
                                                                        link.download = `${getDashboardTitle()}-${selectedGrouping}-${new Date().toISOString().split('T')[0]}.png`;
                                                                        link.href = chartCanvas.toDataURL('image/png');
                                                                        link.click();
                                                                        message.success('Chart exported as PNG');
                                                                    } else {
                                                                        message.error('No chart available to export. Please ensure chart is loaded.');
                                                                    }
                                                                }
                                                            },
                                                            {
                                                                key: 'jpg',
                                                                label: (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ 
                                                                            backgroundColor: '#1890ff', 
                                                                            color: 'white', 
                                                                            padding: '2px 6px', 
                                                                            borderRadius: '3px', 
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold'
                                                                        }}>JPG</span>
                                                                        Compressed image
                                                                    </div>
                                                                ),
                                                                onClick: () => {
                                                                    const chartCanvas = document.querySelector('canvas');
                                                                    if (chartCanvas) {
                                                                        const newCanvas = document.createElement('canvas');
                                                                        const ctx = newCanvas.getContext('2d');
                                                                        newCanvas.width = chartCanvas.width;
                                                                        newCanvas.height = chartCanvas.height;
                                                                        
                                                                        ctx.fillStyle = '#FFFFFF';
                                                                        ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
                                                                        ctx.drawImage(chartCanvas, 0, 0);
                                                                        
                                                                        const link = document.createElement('a');
                                                                        link.download = `${getDashboardTitle()}-${selectedGrouping}-${new Date().toISOString().split('T')[0]}.jpg`;
                                                                        link.href = newCanvas.toDataURL('image/jpeg', 0.9);
                                                                        link.click();
                                                                        message.success('Chart exported as JPG');
                                                                    } else {
                                                                        message.error('No chart available to export. Please ensure chart is loaded.');
                                                                    }
                                                                }
                                                            },
                                                            {
                                                                key: 'pdf',
                                                                label: (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ 
                                                                            backgroundColor: '#ff4d4f', 
                                                                            color: 'white', 
                                                                            padding: '2px 6px', 
                                                                            borderRadius: '3px', 
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold'
                                                                        }}>PDF</span>
                                                                        Document format
                                                                    </div>
                                                                ),
                                                                onClick: async () => {
                                                                    const chartCanvas = document.querySelector('canvas');
                                                                    if (chartCanvas) {
                                                                        try {
                                                                            const jsPDF = (await import('jspdf')).default;
                                                                            const imgData = chartCanvas.toDataURL('image/png');
                                                                            
                                                                            const pdf = new jsPDF({
                                                                                orientation: chartCanvas.width > chartCanvas.height ? 'landscape' : 'portrait',
                                                                                unit: 'mm',
                                                                                format: 'a4'
                                                                            });
                                                                            
                                                                            const pageWidth = pdf.internal.pageSize.getWidth();
                                                                            const pageHeight = pdf.internal.pageSize.getHeight();
                                                                            const margin = 20;
                                                                            const availableWidth = pageWidth - (2 * margin);
                                                                            const availableHeight = pageHeight - (2 * margin) - 30;
                                                                            
                                                                            const imgWidth = availableWidth;
                                                                            const imgHeight = (chartCanvas.height * imgWidth) / chartCanvas.width;
                                                                            
                                                                            pdf.setFontSize(16);
                                                                            pdf.text(getDashboardTitle(), pageWidth / 2, margin, { align: 'center' });
                                                                            
                                                                            pdf.setFontSize(10);
                                                                            pdf.text(`Generated: ${new Date().toLocaleDateString()}`, margin, margin + 15);
                                                                            pdf.text(`Grouping: ${GROUPING_CONFIGS[selectedGrouping].title}`, margin, margin + 25);
                                                                            
                                                                            if (imgHeight <= availableHeight) {
                                                                                pdf.addImage(imgData, 'PNG', margin, margin + 35, imgWidth, imgHeight);
                                                                            } else {
                                                                                const scaledHeight = availableHeight;
                                                                                const scaledWidth = (chartCanvas.width * scaledHeight) / chartCanvas.height;
                                                                                const xOffset = (pageWidth - scaledWidth) / 2;
                                                                                pdf.addImage(imgData, 'PNG', xOffset, margin + 35, scaledWidth, scaledHeight);
                                                                            }
                                                                            
                                                                            pdf.save(`${getDashboardTitle()}-${selectedGrouping}-${new Date().toISOString().split('T')[0]}.pdf`);
                                                                            message.success('Chart exported as PDF');
                                                                        } catch (error) {
                                                                            message.error('PDF export failed. Please install jsPDF: npm install jspdf');
                                                                            console.error('PDF export error:', error);
                                                                        }
                                                                    } else {
                                                                        message.error('No chart available to export. Please ensure chart is loaded.');
                                                                    }
                                                                }
                                                            },
                                                            {
                                                                key: 'data',
                                                                label: (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <span style={{ 
                                                                            backgroundColor: '#722ed1', 
                                                                            color: 'white', 
                                                                            padding: '2px 6px', 
                                                                            borderRadius: '3px', 
                                                                            fontSize: '10px',
                                                                            fontWeight: 'bold'
                                                                        }}>CSV</span>
                                                                        Raw data export
                                                                    </div>
                                                                ),
                                                                onClick: () => {
                                                                    if (currentChartData && currentChartData.length > 0) {
                                                                        const headers = Object.keys(currentChartData[0]);
                                                                        const csvContent = [
                                                                            headers.join(','),
                                                                            ...currentChartData.map(row => 
                                                                                headers.map(header => 
                                                                                    typeof row[header] === 'string' && row[header].includes(',') 
                                                                                        ? `"${row[header]}"` 
                                                                                        : row[header]
                                                                                ).join(',')
                                                                            )
                                                                        ].join('\n');
                                                                        
                                                                        const blob = new Blob([csvContent], { type: 'text/csv' });
                                                                        const url = URL.createObjectURL(blob);
                                                                        const link = document.createElement('a');
                                                                        link.href = url;
                                                                        link.download = `${getDashboardTitle()}-${selectedGrouping}-${new Date().toISOString().split('T')[0]}.csv`;
                                                                        link.click();
                                                                        URL.revokeObjectURL(url);
                                                                        message.success('Data exported as CSV');
                                                                    } else {
                                                                        message.error('No data available to export');
                                                                    }
                                                                }
                                                            }
                                                        ]
                                                    }}
                                                    trigger={['click']}
                                                    placement="bottomLeft"
                                                >
                                                    <Button 
                                                        size="small" 
                                                        icon={<DownloadOutlined />}
                                                        type="primary"
                                                        style={{ 
                                                            backgroundColor: '#52c41a',
                                                            borderColor: '#52c41a'
                                                        }}
                                                    >
                                                        Export
                                                    </Button>
                                                </Dropdown>
                                                <Button 
                                                    size="small" 
                                                    icon={<FilePdfOutlined />}
                                                    onClick={() => {
                                                        const reportContent = `
DASHBOARD REPORT
Generated: ${new Date().toLocaleDateString()}

Dashboard: ${getDashboardTitle()}
Current Tab: ${tab}
Grouping: ${GROUPING_CONFIGS[selectedGrouping].title}
Chart Type: ${chartType.charAt(0).toUpperCase() + chartType.slice(1)}

DATA SUMMARY:
${currentChartData && currentChartData.length > 0 ? 
    currentChartData.map((item, index) => 
        `${index + 1}. ${item.client_name || item.item_station_point || item.item || 'N/A'}: ${item.totalitems || 0} items`
    ).join('\n') 
    : 'No data available'
}

ANNOTATIONS:
${annotations.filter(a => a.grouping === selectedGrouping && a.tab === tab).length > 0 ?
    annotations.filter(a => a.grouping === selectedGrouping && a.tab === tab).map((annotation, index) => 
        `${index + 1}. [${new Date(annotation.timestamp).toLocaleDateString()}] ${annotation.text}`
    ).join('\n')
    : 'No annotations for this view'
}
                                                        `;
                                                        
                                                        const blob = new Blob([reportContent], { type: 'text/plain' });
                                                        const url = URL.createObjectURL(blob);
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.download = `${getDashboardTitle()}-Report-${new Date().toISOString().split('T')[0]}.txt`;
                                                        link.click();
                                                        URL.revokeObjectURL(url);
                                                        message.success('Dashboard report exported');
                                                    }}
                                                >
                                                    Report
                                                </Button>
                                            </Space>
                                        </Col>
                                        
                                        <Col xs={24} sm={6}>
                                            <Space>
                                                <Button 
                                                    size="small" 
                                                    icon={<CommentOutlined />}
                                                    onClick={() => setShowAnnotationModal(true)}
                                                >
                                                    Add Note
                                                </Button>
                                                <Popover
                                                    title="Annotations"
                                                    content={
                                                        <div style={{ maxWidth: '300px', maxHeight: '200px', overflowY: 'auto' }}>
                                                            {annotations.filter(a => a.grouping === selectedGrouping && a.tab === tab).length === 0 ? (
                                                                <div>No annotations for this view</div>
                                                            ) : (
                                                                <List
                                                                    size="small"
                                                                    dataSource={annotations.filter(a => a.grouping === selectedGrouping && a.tab === tab)}
                                                                    renderItem={annotation => (
                                                                        <List.Item
                                                                            actions={[
                                                                                <Button 
                                                                                    size="small" 
                                                                                    type="text" 
                                                                                    danger 
                                                                                    icon={<DeleteOutlined />}
                                                                                    onClick={() => deleteAnnotation(annotation.id)}
                                                                                />
                                                                            ]}
                                                                        >
                                                                            <div>
                                                                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{annotation.text}</div>
                                                                                <div style={{ fontSize: '10px', opacity: 0.7 }}>
                                                                                    {new Date(annotation.timestamp).toLocaleString()}
                                                                                </div>
                                                                            </div>
                                                                        </List.Item>
                                                                    )}
                                                                />
                                                            )}
                                                        </div>
                                                    }
                                                    trigger="click"
                                                >
                                                    <Badge count={annotations.filter(a => a.grouping === selectedGrouping && a.tab === tab).length}>
                                                        <Button size="small">Notes</Button>
                                                    </Badge>
                                                </Popover>
                                            </Space>
                                        </Col>
                                        
                                        <Col xs={24} sm={6}>
                                            <Space>
                                                <Button 
                                                    size="small" 
                                                    type={comparisonMode ? 'primary' : 'default'}
                                                    icon={<SwapOutlined />}
                                                    onClick={() => setComparisonMode(!comparisonMode)}
                                                >
                                                    Compare Mode
                                                </Button>
                                                <Button 
                                                    size="small" 
                                                    type={darkMode ? 'primary' : 'default'}
                                                    icon={darkMode ? '🌙' : '☀️'}
                                                    onClick={() => setDarkMode(!darkMode)}
                                                    style={{
                                                        backgroundColor: darkMode ? '#722ed1' : '#faad14',
                                                        borderColor: darkMode ? '#722ed1' : '#faad14',
                                                        color: 'white'
                                                    }}
                                                >
                                                    {darkMode ? 'Dark' : 'Light'}
                                                </Button>
                                                <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>
                                                    {realTimeEnabled ? 'Live Data' : 'Static Data'}
                                                </div>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>
                            </>
                        )
                    }))}
                />

                <Modal
                    title="Save Current View"
                    open={showSaveViewModal}
                    onOk={saveCurrentView}
                    onCancel={() => {
                        setShowSaveViewModal(false);
                        setViewName('');
                    }}
                    okText="Save"
                >
                    <div style={{ marginBottom: '16px' }}>
                        <strong>Current Configuration:</strong>
                        <ul style={{ marginTop: '8px', fontSize: '12px' }}>
                            <li>Tab: {tab}</li>
                            <li>Grouping: {GROUPING_CONFIGS[selectedGrouping].title}</li>
                            <li>Chart Type: {chartType.charAt(0).toUpperCase() + chartType.slice(1)}</li>
                        </ul>
                    </div>
                    <Input
                        placeholder="Enter view name"
                        value={viewName}
                        onChange={(e) => setViewName(e.target.value)}
                        onPressEnter={saveCurrentView}
                    />
                </Modal>

                <Modal
                    title="Add Annotation"
                    open={showAnnotationModal}
                    onOk={addAnnotation}
                    onCancel={() => {
                        setShowAnnotationModal(false);
                        setAnnotationText('');
                    }}
                    okText="Add Note"
                >
                    <Input.TextArea
                        placeholder="Enter your note or observation about this chart..."
                        value={annotationText}
                        onChange={(e) => setAnnotationText(e.target.value)}
                        rows={4}
                    />
                </Modal>
                
                {realTimeEnabled && (
                    <Alert
                        message="Real-time Mode Active"
                        description="Dashboard is updating automatically every 5 seconds"
                        type="success"
                        showIcon
                        style={{ 
                            marginBottom: '16px', 
                            borderRadius: '8px',
                            animation: 'fadeIn 0.5s ease'
                        }}
                        action={
                            <Button size="small" onClick={() => setRealTimeEnabled(false)}>
                                Disable
                            </Button>
                        }
                    />
                )}
            </div>

            <ChatFloatingButton 
                onClick={() => setChatVisible(true)}
                unreadCount={chatUnreadCount}
                darkMode={darkMode}
            />

            <DashboardChatbot
                visible={chatVisible}
                onClose={() => setChatVisible(false)}
                dashboardData={currentChartData}
                currentTab={tab}
                selectedGrouping={selectedGrouping}
                darkMode={darkMode}
            />

            <style jsx>{`
                .modern-dashboard {
                    transition: all 0.3s ease;
                }
                
                .modern-dashboard.dark-mode {
                    background-color: #0f0f0f;
                    color: #ffffff;
                }
                
                .dark-mode .page-head h2 {
                    color: #ffffff;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                @keyframes slideIn {
                    from { opacity: 0; transform: translateX(-20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                
                .animate-fade-in {
                    animation: fadeIn 0.5s ease forwards;
                }
                
                .animate-slide-in {
                    animation: slideIn 0.5s ease forwards;
                }
                
                .dark-mode .ant-card {
                    background-color: #1f1f1f;
                    border-color: #333;
                    color: #ffffff;
                }
                
                .dark-mode .ant-card-head-title {
                    color: #ffffff;
                }
                
                .dark-mode .ant-tabs-nav {
                    background-color: #1f1f1f;
                }
                
                .dark-mode .ant-tabs-tab {
                    color: #ffffff;
                }
                
                .dark-mode .ant-tabs-tab-active {
                    background-color: #333;
                }
            `}</style>
        </div>
    );
};

function ConsolidatedAssetChart({ 
    groupingType, 
    chartType = 'bar', 
    darkMode = false, 
    onChartTypeChange, 
    selectedGrouping, 
    onGroupingChange, 
    onDataChange,
    ...props 
}) {
    const [result, setResult] = useState({ data: [], page: {} });
    const [loading, setLoading] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showDataLabels, setShowDataLabels] = useState(false);
    const [chartAnimation, setChartAnimation] = useState(false);
    const [cachedData, setCachedData] = useState(new Map());
    const sdataRef = useRef({ p: 1, ps: 25 });
    const chartRef = useRef(null);
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const isNational = util.isNational();

    const config = GROUPING_CONFIGS[selectedGrouping || groupingType];
    const assetConfig = ASSET_CONFIGS[props.asset_cat_id];

    useEffect(() => {
        if (result.data && onDataChange) {
            onDataChange(result.data);
        }
    }, [result.data, onDataChange]);

    // NEW: Scatter data processing
    const getScatterData = () => {
        if (!result.data || result.data.length === 0) return { datasets: [] };
        
        const scatterPoints = result.data.flatMap((item, index) => {
            return assetConfig.datasets.map((dataset, datasetIndex) => ({
                x: index * 10 + datasetIndex * 2,
                y: item[dataset.key] || 0,
                label: `${item[config.labelKey]} - ${dataset.label}`
            }));
        });

        return {
            datasets: [{
                label: 'Asset Distribution',
                data: scatterPoints,
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
                borderColor: 'rgba(75, 192, 192, 1)',
                pointRadius: 6,
                pointHoverRadius: 8
            }]
        };
    };

    // NEW: Year over Year data processing
    const getYoYData = () => {
        if (!result.data || result.data.length === 0) return { labels: [], datasets: [] };
        
        const labels = result.data.map(v => v[config.labelKey]);
        
        const currentYearData = result.data.map(v => {
            return assetConfig.datasets.reduce((sum, dataset) => sum + (v[dataset.key] || 0), 0);
        });
        
        const lastYearData = currentYearData.map(v => Math.round(v * (0.8 + Math.random() * 0.3)));

        return {
            labels,
            datasets: [
                {
                    type: 'bar',
                    label: '2024 (Current)',
                    data: currentYearData,
                    backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                },
                {
                    type: 'bar',
                    label: '2023 (Previous)',
                    data: lastYearData,
                    backgroundColor: 'rgba(255, 159, 64, 0.7)',
                    borderColor: 'rgba(255, 159, 64, 1)',
                    borderWidth: 1
                },
                {
                    type: 'line',
                    label: 'Growth Trend',
                    data: currentYearData.map((v, i) => v - lastYearData[i]),
                    borderColor: 'rgba(75, 192, 192, 1)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderWidth: 3,
                    fill: false,
                    tension: 0.4,
                    pointRadius: 6,
                    yAxisID: 'y1'
                }
            ]
        };
    };

    // NEW: Pivot table data processing
    const getPivotTableData = () => {
        if (!result.data || result.data.length === 0) return { columns: [], data: [] };
        
        const columns = [
            { 
                title: config.chartLabels.x.split(' ')[0], 
                dataIndex: config.labelKey, 
                key: config.labelKey, 
                fixed: 'left', 
                width: 150,
                render: (text) => <strong>{text}</strong>
            }
        ];

        assetConfig.datasets.forEach(dataset => {
            columns.push({
                title: dataset.label,
                dataIndex: dataset.key,
                key: dataset.key,
                width: 120,
                render: (value) => <span style={{ fontWeight: 'bold' }}>{value || 0}</span>
            });
        });

        columns.push({
            title: 'Total',
            key: 'total',
            width: 100,
            render: (_, record) => {
                const total = assetConfig.datasets.reduce((sum, dataset) => 
                    sum + (record[dataset.key] || 0), 0
                );
                return <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{total}</span>;
            }
        });

        const dataWithKeys = result.data.map((item, index) => ({
            ...item,
            key: index
        }));

        const summaryRow = { 
            [config.labelKey]: 'TOTAL', 
            key: 'summary' 
        };
        assetConfig.datasets.forEach(dataset => {
            summaryRow[dataset.key] = result.data.reduce((sum, item) => 
                sum + (item[dataset.key] || 0), 0
            );
        });

        return { 
            columns, 
            data: [...dataWithKeys, summaryRow] 
        };
    };

    const getChartData = () => {
        if (!result.data || result.data.length === 0) return { labels: [], datasets: [] };
        
        if (chartType === 'scatter') return getScatterData();
        if (chartType === 'yoy') return getYoYData();
        
        const labels = result.data.map(v => v[config.labelKey]);
        if (chartType === 'pie') {
        
        const aggregatedData = assetConfig.datasets.map(dataset => {
            return result.data.reduce((sum, item) => sum + (item[dataset.key] || 0), 0);
        });
        
        return {
            labels: assetConfig.datasets.map(d => d.label),
            datasets: [{
                data: aggregatedData,
                backgroundColor: assetConfig.datasets.map(d => d.color),
                borderColor: assetConfig.datasets.map(d => d.color),
                borderWidth: 1
            }]
        };
    }
        return {
            labels,
            datasets: assetConfig.datasets.map(dataset => ({
                label: dataset.label,
                data: result.data.map(v => v[dataset.key] || 0),
                backgroundColor: chartType === 'pie' 
                    ? assetConfig.datasets.map(d => d.color)
                    : dataset.color,
                borderColor: dataset.color,
                borderWidth: chartType === 'line' ? 3 : 1,
                fill: chartType === 'line' ? false : true,
                tension: chartType === 'line' ? 0.4 : 0,
                pointRadius: chartType === 'line' ? 6 : 0,
                pointHoverRadius: chartType === 'line' ? 8 : 0,
                pointBackgroundColor: chartType === 'line' ? dataset.color : undefined,
                pointBorderColor: chartType === 'line' ? '#fff' : undefined,
                pointBorderWidth: chartType === 'line' ? 2 : 0,
                stack: chartType === 'stacked' ? 'stack1' : undefined
            }))
        };
    };

    const processedChartData = useMemo(() => {
        if (!result.data || result.data.length === 0) return null;
        
        const cacheKey = `${groupingType}-${props.asset_cat_id}-${JSON.stringify(result.data)}`;
        if (cachedData.has(cacheKey)) {
            return cachedData.get(cacheKey);
        }
        
        const processedData = getChartData();
        setCachedData(prev => {
            const newCache = new Map(prev);
            newCache.set(cacheKey, processedData);
            if (newCache.size > 10) {
                const firstKey = newCache.keys().next().value;
                newCache.delete(firstKey);
            }
            return newCache;
        });
        
        return processedData;
    }, [result.data, chartType, showDataLabels, groupingType]);

    const AdvancedChartControls = () => (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px',
            padding: '12px',
            background: config.backgroundColor,
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '8px'
        }}>
            <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flex: 1
            }}>
                <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    {config.icon}
                    {config.title}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', opacity: 0.8 }}>Group by:</span>
                    <Radio.Group 
                        value={selectedGrouping} 
                        onChange={(e) => onGroupingChange?.(e.target.value)}
                        size="small"
                    >
                        {Object.entries(GROUPING_CONFIGS).map(([key, configItem]) => (
                            <Radio key={key} value={key} style={{ fontSize: '11px', marginRight: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                    <span style={{ fontSize: '10px' }}>{configItem.icon}</span>
                                    <span style={{ fontSize: '11px' }}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                                </div>
                            </Radio>
                        ))}
                    </Radio.Group>
                </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Space>
                    <span style={{ fontSize: '12px', opacity: 0.8 }}>Chart Type:</span>
                    <Select 
                        size="small" 
                        value={chartType} 
                        onChange={(value) => onChartTypeChange?.(value)}
                        style={{ width: 160 }}
                    >
                        <Select.Option value="bar"><BarChartOutlined /> Bar</Select.Option>
                        <Select.Option value="line"><LineChartOutlined /> Line</Select.Option>
                        <Select.Option value="pie"><PieChartOutlined /> Pie</Select.Option>
                        <Select.Option value="stacked"><ColumnHeightOutlined /> Stacked</Select.Option>
                        <Select.Option value="yoy"><AreaChartOutlined /> Year over Year</Select.Option>
                        <Select.Option value="scatter"><DotChartOutlined /> Scatter</Select.Option>
                        <Select.Option value="pivot"><TableOutlined /> Pivot Table</Select.Option>
                    </Select>
                </Space>

                <AntTooltip title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}>
                    <Button 
                        size="small"
                        icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                        onClick={() => setIsFullscreen(!isFullscreen)}
                    />
                </AntTooltip>
            </div>
        </div>
    );

    // UPDATED: Chart options with support for new chart types
    const getChartOptions = () => {
        const baseOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            animation: chartAnimation ? {
                duration: 1000,
                easing: 'easeOutQuart',
            } : false,
            plugins: {
                legend: {
                    position: chartType === 'pie' ? 'right' : 'top',
                    labels: {
                        usePointStyle: true,
                        padding: 20,
                        font: {
                            size: 12,
                            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                        },
                        color: darkMode ? '#fff' : '#333'
                    }
                },
                tooltip: {
                    backgroundColor: darkMode ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)',
                    titleColor: darkMode ? '#fff' : '#333',
                    bodyColor: darkMode ? '#fff' : '#333',
                    borderColor: darkMode ? '#333' : '#ccc',
                    borderWidth: 1,
                    cornerRadius: 8,
                    displayColors: true,
                    bodyFont: { size: 13 },
                    titleFont: { size: 14, weight: 'bold' },
                    callbacks: {
                        title: (tooltipItems) => tooltipItems[0]?.label || '',
                        label: (context) => `${context.dataset.label}: ${context.parsed.y || context.parsed}`,
                        footer: (tooltipItems) => {
                            const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || item.parsed), 0);
                            return `Total: ${total}`;
                        }
                    }
                }
            },
            scales: chartType === 'pie' || chartType === 'scatter' ? {} : {
                x: {
                    beginAtZero: true,
                    stacked: chartType === 'stacked',
                    title: {
                        display: true,
                        text: config.chartLabels.x,
                        font: { size: 14, weight: 'bold' },
                        color: darkMode ? '#fff' : '#333'
                    },
                    grid: {
                        color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        drawBorder: false
                    },
                    ticks: {
                        color: darkMode ? '#fff' : '#333',
                        maxRotation: 45,
                        minRotation: 0
                    }
                },
                y: {
                    beginAtZero: true,
                    stacked: chartType === 'stacked',
                    title: {
                        display: true,
                        text: config.chartLabels.y,
                        font: { size: 14, weight: 'bold' },
                        color: darkMode ? '#fff' : '#333'
                    },
                    grid: {
                        color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        drawBorder: false
                    },
                    ticks: {
                        color: darkMode ? '#fff' : '#333',
                        maxTicksLimit: 10
                    }
                },
                ...(chartType === 'yoy' && {
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        title: {
                            display: true,
                            text: 'Growth',
                            color: darkMode ? '#fff' : '#333'
                        },
                        grid: {
                            drawOnChartArea: false
                        },
                        ticks: {
                            color: darkMode ? '#fff' : '#333'
                        }
                    }
                })
            },
        };

        if (chartType === 'scatter') {
            return {
                ...baseOptions,
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        title: {
                            display: true,
                            text: 'Index',
                            color: darkMode ? '#fff' : '#333'
                        },
                        grid: {
                            color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                        },
                        ticks: {
                            color: darkMode ? '#fff' : '#333'
                        }
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Count',
                            color: darkMode ? '#fff' : '#333'
                        },
                        grid: {
                            color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                        },
                        ticks: {
                            color: darkMode ? '#fff' : '#333'
                        }
                    }
                }
            };
        }

        if (chartType === 'pie') {
            return {
                ...baseOptions,
                plugins: {
                    ...baseOptions.plugins,
                    legend: {
                        position: 'right',
                        labels: {
                            ...baseOptions.plugins.legend.labels,
                            generateLabels: (chart) => {
                                const data = chart.data;
                                if (data.labels.length && data.datasets.length) {
                                    return data.labels.map((label, i) => {
                                        const dataset = data.datasets[0];
                                        const value = dataset.data[i];
                                        
                                        return {
                                            text: `${label}: ${value}`,
                                            fillStyle: dataset.backgroundColor[i],
                                            strokeStyle: dataset.backgroundColor[i],
                                            lineWidth: 0,
                                            hidden: isNaN(value),
                                            index: i
                                        };
                                    });
                                }
                                return [];
                            }
                        }
                    },
                    tooltip: {
                        ...baseOptions.plugins.tooltip,
                        callbacks: {
                            label: (context) => {
                                return `${context.label}: ${context.parsed}`;
                            },
                        }
                    }
                }
            };
        }

        return baseOptions;
    };

    const list = async (p, ps) => {
        const currentGroupingType = selectedGrouping || groupingType;
        const currentConfig = GROUPING_CONFIGS[currentGroupingType];
        
        setLoading(true);
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.cat_type = props.cat_type;
        sdataRef.current.cat = props.cat;
        sdataRef.current.asset_cat_id = props.asset_cat_id;

        const filterParams = {
            region: sdataRef.current.region,
            site_type: sdataRef.current.site_type,
            client_id: sdataRef.current.client_id,
            station_point_ids: sdataRef.current.station_point_ids,
            item_id: sdataRef.current.item_id,
            functional_status: sdataRef.current.functional_status
        };
        
        const cacheKey = `${currentConfig.apiMethod}-${p}-${ps}-${props.asset_cat_id}-${JSON.stringify(filterParams)}`;
        
        if (cachedData.has(cacheKey) && !loading) {
            const cached = cachedData.get(cacheKey);
            if (Date.now() - cached.timestamp < 30000) {
                setResult(cached.data);
                setLoading(false);
                return;
            }
        }

        try {
            console.log(`Calling ${currentConfig.apiMethod} with filters:`, sdataRef.current);
            const { data } = await AssetInventoryService[currentConfig.apiMethod](sdataRef.current);
            
            if (!data.result || !data.result.data) {
                setResult({ data: [], page: {} });
                return;
            }
            
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            const sortedData = filteredData.sort((a, b) => (b.totalitems || 0) - (a.totalitems || 0));
            
            const resultData = { ...data.result, data: sortedData };
            setResult(resultData);
            
            setCachedData(prev => {
                const newCache = new Map(prev);
                newCache.set(cacheKey, {
                    data: resultData,
                    timestamp: Date.now()
                });
                return newCache;
            });
            
        } catch (error) {
            console.error(`[${currentGroupingType}] API Error:`, error);
            message.error(`Failed to load ${currentGroupingType} data: ${error.message}`);
            setResult({ data: [], page: {} });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => setClients(props.clients), [props.clients]);
    useEffect(() => {
        const filteredItems = props.items.filter(v => v.asset_cat_id === props.asset_cat_id);
        setItems(filteredItems);
    }, [props.items, props.cat_type, props.asset_cat_id]);
    useEffect(() => setStationPoints(props.stationPoints), [props.stationPoints]);
    useEffect(() => setFunctionalStatus(props.functionalStatus), [props.functionalStatus]);

    useEffect(() => {
        list();
        return () => { 
            message.destroy(); 
            setCachedData(new Map());
        };
    }, [props.asset_cat_id, selectedGrouping]);

    // UPDATED: Render chart with all new types
    const renderChart = () => {
        if (chartType === 'pivot') {
            const { columns, data } = getPivotTableData();
            return (
                <Table
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="small"
                    style={{ background: darkMode ? '#1a1a1a' : '#fff' }}
                    rowClassName={(record) => record.key === 'summary' ? 'pivot-summary-row' : ''}
                />
            );
        }

        if (!processedChartData || (processedChartData.labels?.length === 0 && !processedChartData.datasets?.[0]?.data?.length)) {
            return null;
        }

        const chartOptions = getChartOptions();
        const commonProps = {
            ref: chartRef,
            data: processedChartData,
            options: chartOptions,
            redraw: false
        };

        switch (chartType) {
            case 'line':
            case 'yoy':
                return <Line {...commonProps} />;
            case 'pie':
                return <Pie {...commonProps} />;
            case 'scatter':
                return <Scatter {...commonProps} />;
            case 'stacked':
            case 'bar':
            default:
                return <Bar {...commonProps} />;
        }
    };

    return (
        <div className="enhanced-chart-section">
            <div className="page-pad" style={{ backgroundColor: config.backgroundColor }}>
                <AdvancedChartControls />
                
                {result.data.length > 0 && (
                    <div className="text-secondary my-auto" style={{ marginBottom: '15px' }}>
                        Total records: {result.page.total_records}
                    </div>
                )}

                <div className="d-flex tbl-search-head" style={{ marginBottom: '20px' }}>
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm
                            dataRef={sdataRef}
                            onSearch={list}
                            clients={clients}
                            functionalStatus={functionalStatus}
                            stationPoints={stationPoints}
                            cat_type={props.cat_type}
                            asset_cat_id={props.asset_cat_id}
                            isNational={isNational}
                        />
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div 
                        className={`enhanced-chart-container ${isFullscreen ? 'fullscreen-chart' : ''} ${darkMode ? 'dark-chart' : ''}`}
                        style={{
                            width: '100%',
                            height: isFullscreen ? '90vh' : chartType === 'pivot' ? 'auto' : '500px',
                            border: `2px solid ${darkMode ? '#333' : '#e1e1e1'}`,
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '30px',
                            background: darkMode ? '#1a1a1a' : '#ffffff',
                            boxShadow: darkMode 
                                ? '0 4px 20px rgba(0,0,0,0.3)' 
                                : '0 4px 20px rgba(0,0,0,0.1)',
                            position: isFullscreen ? 'fixed' : 'relative',
                            top: isFullscreen ? '5%' : 'auto',
                            left: isFullscreen ? '5%' : 'auto',
                            right: isFullscreen ? '5%' : 'auto',
                            bottom: isFullscreen ? '5%' : 'auto',
                            zIndex: isFullscreen ? 9999 : 'auto',
                            transition: 'all 0.3s ease',
                            overflow: 'hidden'
                        }}
                    >
                        {loading && (
                            <div style={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                background: 'rgba(0,0,0,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                zIndex: 10
                            }}>
                                <div style={{
                                    background: darkMode ? '#333' : '#fff',
                                    padding: '20px',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    color: darkMode ? '#fff' : '#333'
                                }}>
                                    Loading chart data...
                                </div>
                            </div>
                        )}

                        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                            {renderChart()}
                        </div>

                        {isFullscreen && (
                            <Button
                                type="primary"
                                icon={<CompressOutlined />}
                                onClick={() => setIsFullscreen(false)}
                                style={{
                                    position: 'absolute',
                                    top: '10px', left: '10px',
                                    zIndex: 1000
                                }}
                            >
                                Exit Fullscreen
                            </Button>
                        )}
                    </div>
                </If>
                
                <If cond={!result.data.length && !loading}>
                    <div className="no-rec" style={{ 
                        textAlign: 'center', 
                        padding: '50px',
                        background: darkMode ? '#1a1a1a' : '#f9f9f9',
                        borderRadius: '12px',
                        border: `1px solid ${darkMode ? '#333' : '#e1e1e1'}`
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }}>
                            📊
                        </div>
                        <div style={{ fontSize: '18px', marginBottom: '8px' }}>
                            No data available
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.6 }}>
                            Try adjusting your filters or check back later
                        </div>
                    </div>
                </If>
            </div>

            <style jsx>{`
                .enhanced-chart-container {
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    will-change: transform;
                }
                
                .enhanced-chart-container:hover:not(.fullscreen-chart) {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                }
                
                .fullscreen-chart {
                    backdrop-filter: blur(10px);
                    animation: zoomIn 0.3s ease-out;
                }
                
                @keyframes zoomIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                
                .dark-chart {
                    border-color: #333 !important;
                    background: #1a1a1a !important;
                }
                
                .pivot-summary-row {
                    background: ${darkMode ? '#2a2a2a' : '#f0f0f0'} !important;
                    font-weight: bold;
                }
                
                .pivot-summary-row td {
                    border-top: 2px solid ${darkMode ? '#444' : '#d9d9d9'} !important;
                }
            `}</style>
        </div>
    );
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational, cat_type, asset_cat_id } = props;
    let [data, setData] = useState({ ...dataRef.current, station_point_ids: [], isdonated: null });
    let [stationPoints, setStationPoints] = useState([]);
    let [functionalStatus, setFunctionalStatus] = useState([]);
    const [items, setItems] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.station_point_ids = [];
        }
        setData({ ...data });
    };

    const handleReset = () => {
        const resetData = {
            p: 1,
            ps: 25,
            cat_type: data.cat_type,
            cat: data.cat,
            asset_cat_id: data.asset_cat_id,
            region: null,
            site_type: null,
            client_id: isNational === 1 ? null : clients.find(v => v.is_me)?.id,
            station_point_ids: [],
            item_id: null,
            functional_status: null,
            isdonated: null
        };
        setData(resetData);
        dataRef.current = resetData;
        onSearch();
    };

    const handleSearch = () => {
        dataRef.current = { ...data };
        onSearch();
    };

    useEffect(() => {
        dataRef.current = { ...data };
    }, [data, dataRef]);

    useEffect(() => {
        if (isNational === 1) {
            setData({ ...data, client_id: null });
        } else {
            let ob = clients.find(v => v.is_me);
            setData({ ...data, client_id: ob?.id });
        }
    }, [clients]);

useEffect(() => {
    setStationPoints([...props.stationPoints]);
}, [props.stationPoints]);

useEffect(() => {
    setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
}, [dataRef.current.p, dataRef.current.ps]);

useEffect(() => {
    setFunctionalStatus([...props.functionalStatus]);
}, [props.functionalStatus]);

useEffect(() => {
    AssetService.allItems({ 
        status: 1, 
        cat_type, 
        asset_cat_id, 
        client_id: data.client_id, 
        availableAssetsOnly: 'Y' 
    }).then(res => {
        let allItems = res.data.result.data.map((v) => {
            if (v.cat_type === 'Med/NonMed') {
                v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
            } else {
                v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
            }
            return v;
        });
        setItems(allItems);
    }).catch(error => {
        message.error('Failed to load items: ' + error.message);
    });
}, [cat_type, data.client_id, asset_cat_id]);

const getFieldLabel = () => {
    switch (asset_cat_id) {
        case '8': return 'Make';
        case '3': return 'Medical Asset';
        case '2': return 'Non Medical Asset';
        default: return 'Asset';
    }
};

const getStatusLabel = () => {
    switch (asset_cat_id) {
        case '8': return 'Vehicle Status';
        case '3': return 'Functional Status';
        case '2': return 'Functional Status';
        default: return 'Status';
    }
};

return (
    <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
        <div className="d-flex">
            {isNational === 1 && (
                <div className="w210 mr5">
                    <div className="text-secondary fs11 mb2">Region</div>
                    <AntdSelect
                        options={[
                            { value: 'Manzini', label: "Manzini" },
                            { value: 'Hhohho', label: "Hhohho" },
                            { value: 'Shiselweni', label: "Shiselweni" },
                            { value: 'Lubombo', label: "Lubombo" }
                        ]}
                        allowClear
                        value={data.region}
                        onChange={v => { handleChange(v, 'region') }}
                    />
                </div>
            )}
            {isNational === 1 && (
                <div className="w210 mr5">
                    <div className="text-secondary fs11 mb2">Level</div>
                    <AntdSelect
                        options={[
                            { value: 'Level 1', label: "Level 1" },
                            { value: 'Level 2', label: "Level 2" },
                            { value: 'Level 3', label: "Level 3" },
                            { value: 'Level 4', label: "Level 4" },
                            { value: 'Level 5', label: "Level 5" }
                        ]}
                        allowClear
                        value={data.site_type}
                        onChange={v => { handleChange(v, 'site_type') }}
                    />
                </div>
            )}

            {isNational === 1 && (
                <div className="w300 mr5">
                    <div className="text-secondary fs11 mb2">Facility</div>
                    <AntdSelect
                        placeholder="All Facilities"
                        allowClear
                        showSearch
                        options={clients}
                        value={data.client_id}
                        onChange={v => {
                            data.item_id = null;
                            handleChange(v, 'client_id');
                        }}
                    />
                </div>
            )}

            <div className="w200 mr5">
                <div className="text-secondary fs11 mb2">Location</div>
                <AntdSelect
                    showSearch
                    allowClear
                    placeholder="Location"
                    mode="multiple"
                    options={stationPoints.filter(v => v.client_ids && v.client_ids.includes(data.client_id))}
                    value={data.station_point_ids}
                    onChange={v => {
                        handleChange(v, 'station_point_ids');
                    }}
                />
            </div>

            <div className="w350 mr5">
                <div className="text-secondary fs11 mb2">{getFieldLabel()}</div>
                <AntdSelect
                    placeholder="Item (All)"
                    allowClear
                    showSearch
                    options={items.map(v => { return { id: v.id, name: v.item_full_name } })}
                    value={data.item_id}
                    onChange={v => handleChange(v, 'item_id')}
                />
            </div>

            <div className="w200 mr5">
                <div className="text-secondary fs11 mb2">{getStatusLabel()}</div>
                <AntdSelect
                    placeholder="Status"
                    showSearch
                    allowClear
                    options={functionalStatus}
                    value={data.functional_status}
                    onChange={v => handleChange(v, 'functional_status')}
                />
            </div>

            <div className="pt15">
                <Button className="mt4" type="primary" onClick={handleSearch}>
                    <img src={search} alt='search' className='h-1 mr5' height={15} />
                </Button>
                <Button 
                    className="mt4 ml5" 
                    type="default" 
                    onClick={handleReset}
                    icon={<ReloadOutlined />}
                >
                    Reset
                </Button>
            </div>
        </div>
    </form>
);
};

export default Dashboards;